import { useCallback, useMemo } from 'react'

import useRenewalEmployees from 'apps/employer_renewals/hooks/useRenewalEmployees'
import { useAllowances } from 'apps/employer_renewals/providers/AllowancesProvider'
import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { useQuoteResults } from 'apps/employer_renewals/providers/QuoteResultsProvider'
import { type Period, type RenewalApproach } from 'apps/employer_renewals/types'
import { type ComplianceCalculations, useComplianceCalculations } from 'components/hooks/quotes/useComplianceCalculations'
import { AllowanceStrategies } from 'pages/employer_onboarding/quote/types'
import { calculateDependentCoveragePortionOfEmployeeMonthlyAllowance } from 'utils/quote_calculations'

import useRenewalQuoteCosts from './useRenewalQuoteCosts'

type QuoteSummary = {
  currentYearCost: number
  renewalYearCost: number
  isLoading: boolean
  complianceCalculations: ComplianceCalculations
  calculateRenewalYearAdditionalDependentCoverageCosts: (spousePercentage: number, additionalDependentPercentage: number) => number
  numEmployeesWithDependents: number
  numDependentsAcrossAllEmployees: number
}

type Props = {
  renewalApproach?: RenewalApproach
  period: Period
}

export default function useQuoteSummary ({
  renewalApproach = 'match',
  period,
}: Props): QuoteSummary {
  const { quoteResults, isLoadingQuoteResults } = useQuoteResults()
  const { enrolledEmployeeIds, isLoading: isLoadingEmployees } = useRenewalEmployees({})
  const { isUsingFlatAllowances, isLoading: isLoadingRenewalConfig } = useEmployerRenewalConfig()
  const { currentYearAllowances, renewalYearAllowances, renewalYearAllowanceParams, employeeFilter } = useAllowances()

  const currentQuoteResults = useMemo(() => {
    if (!quoteResults) {
      return []
    }

    if (employeeFilter === 'enrolled') {
      return quoteResults.current.quote_results.filter((result) => enrolledEmployeeIds.has(result.id))
    }

    return quoteResults.current.quote_results.filter((result) => result.eligible)
  }, [quoteResults, enrolledEmployeeIds, employeeFilter])

  const renewalQuoteResults = useMemo(() => {
    if (!quoteResults) {
      return []
    }

    let results = quoteResults.matched.quote_results

    if (renewalApproach === 'reduce') {
      results = quoteResults.reduced?.quote_results ?? []
    }
    if (renewalApproach === 'upgrade') {
      results = quoteResults.upgraded?.quote_results ?? []
    }
    if (renewalApproach === 'custom') {
      results = quoteResults.custom?.quote_results ?? []
    }

    return results
  }, [quoteResults, renewalApproach])

  const eligibleRenewalQuoteResults = useMemo(() => {
    return renewalQuoteResults.filter((result) => result.eligible)
  }, [renewalQuoteResults])

  const filteredRenewalQuoteResults = useMemo(() => {
    if (employeeFilter === 'eligible') {
      return eligibleRenewalQuoteResults
    }

    if (employeeFilter === 'enrolled') {
      return renewalQuoteResults.filter((result) => enrolledEmployeeIds.has(result.id))
    }

    return renewalQuoteResults
  }, [renewalQuoteResults, eligibleRenewalQuoteResults, enrolledEmployeeIds, employeeFilter])

  const currentQuoteCosts = useRenewalQuoteCosts({ allowances: currentYearAllowances, quoteResults: currentQuoteResults })
  const eligibleRenewalQuoteCosts = useRenewalQuoteCosts({ allowances: isUsingFlatAllowances ? renewalYearAllowanceParams : renewalYearAllowances, quoteResults: eligibleRenewalQuoteResults })
  const filteredRenewalQuoteCosts = useRenewalQuoteCosts({ allowances: isUsingFlatAllowances ? renewalYearAllowanceParams : renewalYearAllowances, quoteResults: filteredRenewalQuoteResults })

  const periodMultiplier = period === 'monthly' ? 1 : 12

  const calculateRenewalYearAdditionalDependentCoverageCosts = useCallback((spousePercentage: number, additionalDependentPercentage: number) => {
    return filteredRenewalQuoteResults.reduce((acc, curr) => (
      acc + calculateDependentCoveragePortionOfEmployeeMonthlyAllowance(curr, spousePercentage / 100, additionalDependentPercentage / 100)
    ), 0) * periodMultiplier
  }, [filteredRenewalQuoteResults, periodMultiplier])

  const numEmployeesWithDependents = useMemo(() => {
    return filteredRenewalQuoteResults.filter((result) => result.dependents.length > 0).length
  }, [filteredRenewalQuoteResults])

  const numDependentsAcrossAllEmployees = useMemo(() => {
    return filteredRenewalQuoteResults.reduce((acc, curr) => acc + curr.dependents.length, 0)
  }, [filteredRenewalQuoteResults])

  const complianceCalculations = useComplianceCalculations(
    eligibleRenewalQuoteResults,
    isUsingFlatAllowances ? AllowanceStrategies.Flat : AllowanceStrategies.Smart,
    renewalYearAllowances[0]?.employee_contribution_percentage ?? 0,
    eligibleRenewalQuoteCosts.flatAllowanceCalculations.costsByEmployeeId
  )

  return {
    currentYearCost: currentQuoteCosts.employerCost * periodMultiplier,
    renewalYearCost: filteredRenewalQuoteCosts.employerCost * periodMultiplier,
    isLoading: isLoadingQuoteResults || isLoadingEmployees || isLoadingRenewalConfig,
    complianceCalculations,
    calculateRenewalYearAdditionalDependentCoverageCosts,
    numEmployeesWithDependents,
    numDependentsAcrossAllEmployees,
  }
}
